// Data-driven choropleth classification (S12). Replaces hardcoded ranges so
// skewed distributions don't flatten the map into one colour.
//
//  - linear     : equal-interval between min and max
//  - quantile   : equal count of regions per class (data-driven breaks)
//  - percentile : equal-interval between the 2nd and 98th percentile (clips
//                 outliers so the bulk of the data uses the full ramp)
//  - log        : equal-ratio between min and max (for metrics spanning
//                 several decades, e.g. density 0.0002 → 1152 hab/km²)
//
// `linear` is a trap on this dataset: density and GDP/capita are so
// right-skewed that equal intervals put ~99% of regions in the first class,
// i.e. an all-white map. `quantile` is the default for that reason.
export const CLASS_METHODS = [
  { key: 'quantile', label: 'Quantiles' },
  { key: 'percentile', label: 'Percentiles (2–98)' },
  { key: 'log', label: 'Logarithmique' },
  { key: 'linear', label: 'Min / Max (linéaire)' },
]

const at = (sorted, frac) => sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor(frac * (sorted.length - 1))))]

// Returns `nClasses - 1` ascending, strictly-increasing break values (the
// thresholds between classes). Empty array if there isn't enough data.
export function computeBreaks(values, method, nClasses) {
  const sorted = values.filter((v) => Number.isFinite(v) && v > 0).sort((a, b) => a - b)
  if (sorted.length < 2 || nClasses < 2) return []
  const nBreaks = nClasses - 1
  const raw = []

  if (method === 'quantile') {
    for (let i = 1; i <= nBreaks; i++) raw.push(at(sorted, i / nClasses))
  } else if (method === 'percentile') {
    const lo = at(sorted, 0.02)
    const hi = at(sorted, 0.98)
    for (let i = 1; i <= nBreaks; i++) raw.push(lo + (hi - lo) * (i / nClasses))
  } else if (method === 'log') {
    // Values are already filtered to > 0, so log() is always defined.
    const lo = Math.log(sorted[0])
    const hi = Math.log(sorted[sorted.length - 1])
    for (let i = 1; i <= nBreaks; i++) raw.push(Math.exp(lo + (hi - lo) * (i / nClasses)))
  } else { // linear
    const min = sorted[0]
    const max = sorted[sorted.length - 1]
    for (let i = 1; i <= nBreaks; i++) raw.push(min + (max - min) * (i / nClasses))
  }

  // MapLibre 'step' requires strictly increasing stops — dedupe by nudging.
  const breaks = []
  let prev = -Infinity
  for (let v of raw) {
    if (v <= prev) v = prev + Math.max(1, Math.abs(prev) * 1e-6)
    breaks.push(v)
    prev = v
  }
  return breaks
}

// Build a MapLibre `step` expression: colors has length breaks.length + 1.
export function buildStepExpression(metricExpr, breaks, colors) {
  const expr = ['step', metricExpr, colors[0]]
  for (let i = 0; i < breaks.length; i++) {
    expr.push(breaks[i], colors[i + 1])
  }
  return expr
}
